//@flow
import * as React from 'react';
import classnames from 'classnames';
import {
    Box,
    Text,
    Column,
    Columns,
    Stack,
    Icon as NeoIcon
} from '@locus-taxy/neo-ui';

import { collapsibleListCardFactory } from 'common/components/core/card/CollapsibleListCard';
import { Loader } from 'common/components/core/loader/Loader';
import SearchInput from 'common/components/core/search_input';
import { SizeEnum } from 'common/enums/UIEnums';
import { type TFunction } from 'react-i18next';
import type { MasterOutlet } from 'common/models/internal/MasterOutlet';
import { type ThemeType } from 'react-css-themr';
import type { TerritoryPaginationType } from './TerritoryPlanningContainer';
import TerritoryPagination from './TerritoryPagination';

type Props = {
    t: TFunction,
    outletsPagination: TerritoryPaginationType,
    updateOutletsPagination: (pagination: TerritoryPaginationType) => void,
    nonDistributorOutlets: Array<MasterOutlet>,
    unallocatedOutletSearchInput: ?string,
    onSearchInputChange: (input: string) => void,
    areNonDistributorOutletsLoading: boolean,
    onPageChange: () => void,
    onOutletSelect: () => void,
    selectedOutlet: ?MasterOutlet,
    theme: ThemeType,
    onUnallocatedCardToggle: () => void,
    allDistributors: Array<string>,
    isExpandible: boolean,
    isExpanded: boolean,
    areNonDistributorOutletsLoading: boolean
};

class UnallocatedOutletCard extends React.PureComponent<Props> {
    render() {
        const {
            onUnallocatedCardToggle,
            theme,
            areNonDistributorOutletsLoading,
            t
        } = this.props;
        return (
            <Box
                height="5vh"
                width="fluid"
                p={3}
                className={theme.distributorHeader}
                onClick={onUnallocatedCardToggle}
            >
                <Columns space="xSmall" alignY="center">
                    <Column width="fluid">
                        <Text fontWeight={600} fontSize={3}>
                            {t('entityManagement:territory.unallocatedOutlets')}
                        </Text>
                    </Column>

                    <Loader
                        theme={theme}
                        size={SizeEnum.Small}
                        active={areNonDistributorOutletsLoading}
                    />
                </Columns>
            </Box>
        );
    }
}

type UnallocatedOutletItemProps = {
    theme: ThemeType,
    outlet: MasterOutlet,
    isSelected: boolean,
    onOutletSelect: (outletId: string) => void,
    areNonDistributorOutletsLoading: boolean
};

class UnallocatedOutletItem extends React.PureComponent<UnallocatedOutletItemProps> {
    onOutletSelct = () => {
        const { outlet, onOutletSelect } = this.props;
        onOutletSelect(outlet.id);
    };
    render() {
        const {
            theme,
            outlet,
            isSelected,
            areNonDistributorOutletsLoading
        } = this.props;
        if (areNonDistributorOutletsLoading) {
            return <Loader active />;
        }
        return (
            <Box
                height="52px"
                p={3}
                className={classnames(theme.distributorItem, {
                    [theme.selectedItem]: isSelected
                })}
                onClick={this.onOutletSelct}
            >
                <Columns space="small" alignY="center">
                    <Column width="content">
                        <NeoIcon size="medium" icon="location_on" />
                    </Column>
                    <Column width="fluid">
                        <Text fontWeight={400} fontSize={3}>
                            {outlet.name || outlet.id}
                        </Text>
                    </Column>
                </Columns>
            </Box>
        );
    }
}

class UnallocatedOutlets extends React.PureComponent<Props> {
    onOutletSearchChange = value => {
        const { onSearchInputChange } = this.props;
        onSearchInputChange(value);
    };

    updatePagination = pagination => {
        const { updateOutletsPagination } = this.props;
        updateOutletsPagination(pagination);
    };

    getContainer = () => {
        return 'unallocatedOutlets';
    };
    render() {
        const {
            nonDistributorOutlets,
            unallocatedOutletSearchInput,
            theme,
            onOutletSelect,
            selectedOutlet,
            areNonDistributorOutletsLoading,
            onPageChange,
            outletsPagination,
            t
        } = this.props;

        return (
            <Box height="41vh">
                <Stack height="fluid">
                    <Box
                        height="60px"
                        className={theme.paginationAndSearch}
                        p={2}
                    >
                        <Columns alignY="center" space="small">
                            <Column width="fluid">
                                <SearchInput
                                    className={theme.searchInput}
                                    onSearchInputChange={
                                        this.onOutletSearchChange
                                    }
                                    value={unallocatedOutletSearchInput}
                                    theme={theme}
                                    placeholder={t(
                                        'entityManagement:territory.searchPlaceholder'
                                    )}
                                />
                            </Column>
                            <Column width="1/4">
                                <TerritoryPagination
                                    areNonDistributorOutletsLoading={
                                        areNonDistributorOutletsLoading
                                    }
                                    getContainer={this.getContainer}
                                    updatePagination={this.updatePagination}
                                    paginationDetails={outletsPagination}
                                    totalPages={outletsPagination.totalPages}
                                    onPageChange={onPageChange}
                                    pageSize={outletsPagination.size}
                                    totalItems={outletsPagination.totalItems}
                                    pageSizeOptions={
                                        outletsPagination.pageSizeOptions
                                    }
                                />
                            </Column>
                        </Columns>
                    </Box>
                    {unallocatedOutletSearchInput &&
                    nonDistributorOutlets.length === 0 &&
                    !areNonDistributorOutletsLoading ? (
                        <Box height="60px">
                            <Columns alignY="center">
                                <Column>
                                    <Text fontSize={3} fontWeight={600}>
                                        {t(
                                            'entityManagement:territory.noOutletsFound'
                                        )}
                                    </Text>
                                </Column>
                            </Columns>
                        </Box>
                    ) : null}
                    <Box height="38vh">
                        {nonDistributorOutlets.map(outlet => {
                            return (
                                <UnallocatedOutletItem
                                    key={outlet.id}
                                    theme={theme}
                                    outlet={outlet}
                                    onOutletSelect={onOutletSelect}
                                    isSelected={
                                        selectedOutlet &&
                                        outlet.id === selectedOutlet.id
                                            ? true
                                            : false
                                    }
                                />
                            );
                        })}
                    </Box>
                </Stack>
            </Box>
        );
    }
}

const UnallocatedOutletList = collapsibleListCardFactory({
    CardComponent: UnallocatedOutletCard,
    CollapseComponent: UnallocatedOutlets
});

export default (UnallocatedOutletList: React.AbstractComponent<Props>);
